import React, { useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { BlockButton } from "../../components/BlockButton/BlockButton";
import { WithCollaboration } from "../../components/Collaboration/Collaboration";
import { Column } from "../../components/Column/Column";
import { Container } from "../../components/Container/Container";
import { Flex } from "../../components/Flex/Flex";
import { Form } from "../../components/Form/Form";
import { FormField, FormFieldOptions } from "../../components/GeneratedForm/GeneratedForm";
import { P } from "../../components/Paragraph/Paragraph";
import { toastState, ToastType } from "../../components/Toast/Toast";
import { AdminViewParams, ADMIN_VIEW_PATH } from "../../routes";
import {
  AdminFeedItemAudienceEnum,
  AdminFeedTopicsQueryResult,
  CollaborationTypeEnum,
  useUpdateAdminFeedItemMutation,
} from "../../schema";
import { buildUrl } from "../../services/buildUrl";
import { getEnumFormOptions } from "../../services/getEnumFormOptions";
import { getMetadataFromFields } from "../../services/getMetadataFromFields";
import { getMetadataFormFields } from "../../services/getMetadataSchema";
import { isFeedItemApproved } from "../../services/isFeedItemApproved";
import { validateMaximumLength } from "../../validators/validateMaximumLength";
import { validateNumeric } from "../../validators/validateNumeric";
import { AdminFeedItemDetailsProps } from "./AdminFeedItemDetailsView";

interface AdminFeedItemDetailsEditProps extends AdminFeedItemDetailsProps {
  topics: NonNullable<AdminFeedTopicsQueryResult["data"]>["adminFeedTopics"];
}

export const AdminFeedItemDetailsEdit: React.FC<AdminFeedItemDetailsEditProps> = ({
  adminFeedItem,
  viewer,
  topics,
}) => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const [updateAdminInfo] = useUpdateAdminFeedItemMutation({
    refetchQueries: ["CollaborationById"],
    awaitRefetchQueries: true,
    onCompleted: ({ updateAdminFeedItem }) => {
      setToast({
        type: ToastType.SUCCESS,
        title: "Admin feed item has been updated",
        message: `Updated admin feed item "${updateAdminFeedItem.type}" details.`,
        isOpen: true,
      });

      navigate({
        pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, {
          menu: "admin-feed",
          page: "items",
          id: adminFeedItem.id,
        }),
      });
    },
  });

  // metadata form configuration
  const metadataFormFields = getMetadataFormFields(adminFeedItem.type, adminFeedItem.metadata);

  const formRef = useRef<HTMLFormElement>(null);
  const formMethods = useForm({
    defaultValues: {
      metadata: metadataFormFields.reduce((acc, cur) => {
        const name = cur.name.split(".")[2];

        //@ts-ignore
        acc.push({ [name]: cur.defaultValue });

        return acc;
      }, []),
    },
  });
  const { handleSubmit } = formMethods;

  // form configuration
  const fields = useMemo<FormFieldOptions[]>(
    () => [
      {
        field: "text",
        type: "text",
        name: "description",
        label: "Description of the feed item",
        rules: {
          validate: validateMaximumLength(255),
        },
        defaultValue: adminFeedItem.description,
      },
      {
        field: "checkbox",
        name: "audience",
        label: "Audience who will see this item",
        options: getEnumFormOptions(AdminFeedItemAudienceEnum),
        rules: { required: "Please choose at least one audience type" },
        defaultValue: adminFeedItem.audience,
      },
      {
        field: "radio",
        name: "isPinned",
        label: "Is pinned to the top",
        options: [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ],
        rules: { required: "Please choose whether the item is pinned to the top" },
        defaultValue: adminFeedItem.isPinned ? "true" : "false",
      },
      {
        field: "radio",
        name: "isRecommended",
        label: "Is feed item recommended",
        options: [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ],
        rules: { required: "Please choose whether the item is recommended" },
        defaultValue: adminFeedItem.isRecommended ? "true" : "false",
      },
      {
        field: "date",
        name: "startDate",
        label: "Show item from given date",
        defaultValue: new Date(adminFeedItem.startDate),
      },
      {
        field: "date",
        name: "endDate",
        label: "Show item until given date (leave empty to not use)",
        defaultValue: adminFeedItem.endDate ? new Date(adminFeedItem.endDate) : undefined,
      },
      {
        field: "text",
        type: "text",
        name: "startDay",
        label: "Show item starting from given days after registration (0 for first day, empty to not use)",
        rules: {
          validate: validateNumeric({
            min: 0,
            optional: true,
            message: "Expected positive start day number",
          }),
        },
        defaultValue: adminFeedItem.startDay ? adminFeedItem.startDay.toString() : undefined,
      },
      {
        field: "text",
        type: "text",
        name: "endDay",
        label: "Show item starting until given days after registration (leave empty to not use)",
        rules: {
          validate: validateNumeric({
            min: 0,
            optional: true,
            message: "Expected positive end day number",
          }),
        },
        defaultValue: adminFeedItem.endDay ? adminFeedItem.endDay.toString() : undefined,
      },
      {
        field: "text",
        type: "text",
        name: "likeCount",
        label: "Like count",
        rules: {
          validate: validateNumeric({
            min: 0,
            optional: true,
            message: "Expected positive like count",
          }),
        },
        defaultValue: adminFeedItem.likeCount.toString(),
      },
      {
        field: "text",
        type: "text",
        name: "shareCount",
        label: "Share count",
        rules: {
          validate: validateNumeric({
            min: 0,
            optional: true,
            message: "Expected positive share count",
          }),
        },
        defaultValue: adminFeedItem.shareCount.toString(),
      },
      {
        field: "checkbox",
        name: "topics",
        label: "Feed item topics",
        options: topics.map((topic) => ({ label: topic.name, value: topic.id })),
        rules: { required: "Please choose at least one topic type" },
        defaultValue: adminFeedItem.topics.map((topic) => topic.id),
      },
      {
        field: "text",
        type: "text",
        name: "version",
        label: "Metadata version",
        rules: { required: "Please provide metadata version" },
        defaultValue: adminFeedItem.version.toString(),
      },
      {
        field: "textarea",
        type: "text",
        name: "comment",
        label: "Collaboration comment",
        rules: {
          required: "Please describe what and why was updated",
        },
        defaultValue: "Updated admin feed item info",
      },
    ],
    [
      adminFeedItem.audience,
      adminFeedItem.endDate,
      adminFeedItem.endDay,
      adminFeedItem.isPinned,
      adminFeedItem.likeCount,
      adminFeedItem.shareCount,
      adminFeedItem.startDate,
      adminFeedItem.startDay,
      adminFeedItem.version,
      adminFeedItem.topics,
      adminFeedItem.isRecommended,
      adminFeedItem.description,
      topics,
    ],
  );

  const onSubmit = async (data: any) =>
    updateAdminInfo({
      variables: {
        ...adminFeedItem,
        ...data,
        adminFeedItemId: adminFeedItem.id,
        isPinned: data.isPinned === "true",
        isRecommended: data.isRecommended === "true",
        startDay: data.startDay ? parseInt(data.startDay, 10) : null,
        endDay: data.endDay ? parseInt(data.endDay, 10) : null,
        likeCount: parseInt(data.likeCount, 10),
        shareCount: parseInt(data.shareCount, 10),
        version: parseInt(data.version, 10),
        metadata: getMetadataFromFields(data.metadata, adminFeedItem.type),
        topicIds: data.topics,
        description: data.description || "",
      },
    });

  return (
    <WithCollaboration referenceId={adminFeedItem.id} type={CollaborationTypeEnum.USER} viewer={viewer}>
      {!isFeedItemApproved(adminFeedItem) ? (
        <FormProvider {...formMethods}>
          <Form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <Flex overflow row={false} expanded={false}>
              {fields.map((field) => (
                <Container overflow key={field.name} expanded>
                  <FormField field={field} />
                </Container>
              ))}

              <h3>Metadata</h3>

              {metadataFormFields.length === 0 && <P>No metadata required</P>}

              {metadataFormFields.map((field) => (
                <Container overflow key={field.name} expanded>
                  <FormField field={field} />
                </Container>
              ))}
            </Flex>

            <Column marginTop="half" crossAxisAlignment="flex-end">
              <BlockButton inline tertiary loading={false} type="submit">
                Update item
              </BlockButton>
            </Column>
          </Form>
        </FormProvider>
      ) : (
        <Container>Feed item is approved. Editing is disabled.</Container>
      )}
    </WithCollaboration>
  );
};
